import { Injectable } from '@angular/core';
import { AppConfig } from '../../core/services/app-config.service';
import { CustomerStoreService } from '../../core/services/customer-store.service';
import { AvailablePaymentMethods } from '../models/available-payment-methods.model';
import { PaymentMethodAvailability } from '../models/payment-method-availability.model';
import { PaymentMethodCode } from '../models/payment-method-code.enum';
import { PaymentMethod } from '../models/payment-method.model';
import { Payment } from '../models/payment.model';
import { Tooltip } from '../models/tooltip.enum';

@Injectable({
  providedIn: 'root',
})
export class PaymentMethodService {
  constructor(private customerStoreService: CustomerStoreService, private appConfig: AppConfig) {
    this.initializePaymentMethods(
      this.customerStoreService.isCustomerLocked(),
    );
    if (!this.appConfig.config.twisto.enabled) {
      this.methods = this.methods.slice(1);
    }
  }

  private methods: PaymentMethod[];

  private static paymentMethodAvailable(methods: AvailablePaymentMethods, method: PaymentMethodCode): boolean {
    return methods[method] && methods[method] === PaymentMethodAvailability.AVAILABLE;
  }

  private static getTooltipForLockedPayment(isCustomerBlocked: boolean): string {
    if (isCustomerBlocked) {
      return Tooltip.CUSTOMER_BLOCKED;
    }
  }

  public getAllVisibleMethods(payment: Payment): PaymentMethod[] {
    if (this.customerStoreService.isCustomerLocked() || !payment || !payment.id) {
      this.methods = this.methods.filter((m) => m.code !== PaymentMethodCode.AUTO_PAYMENT);
      return this.methods;
    }
    if (
      payment.automaticPaymentAvailable &&
      !payment.automaticPaymentConfigured &&
      !this.containsAutoPayment() &&
      this.appConfig.config.payment.autoPayment.enabled
    ) {
      const autoPaymentMethod = {
        available: true,
        code: PaymentMethodCode.AUTO_PAYMENT,
        image: '',
        name: 'Ustaw autopłatność',
        tooltip: '',
      };
      this.methods.splice(1, 0, autoPaymentMethod);
    } else if (!payment.automaticPaymentAvailable || payment.automaticPaymentConfigured) {
      this.methods = this.methods.filter((m) => m.code !== PaymentMethodCode.AUTO_PAYMENT);
    }

    return this.methods.map((method) => this.getAvailableMethod(method, payment));
  }

  private initializePaymentMethods(isCustomerBlocked: boolean): void {
    const tooltipForLockedPayment = PaymentMethodService.getTooltipForLockedPayment(
      isCustomerBlocked,
    );

    this.methods = [
      {
        available: false,
        code: PaymentMethodCode.DEFER,
        image: '/assets/images/logo-payka/logo_payka.svg',
        name: 'Zapłać do 45 dni: ',
        tooltip: tooltipForLockedPayment,
        loadingText: 'W trakcie odraczania',
      },
      {
        available: false,
        code: PaymentMethodCode.TWISTO,
        image: '/assets/images/payment-methods/twisto.png',
        name: 'Zapłać za 30 dni',
        tooltip: tooltipForLockedPayment,
      },
      {
        available: false,
        code: PaymentMethodCode.CREDIT_CARD,
        image: '/assets/images/payment-methods/credit_cards.png',
        name: 'Karta płatnicza',
        tooltip: tooltipForLockedPayment,
      },
      {
        available: false,
        code: PaymentMethodCode.BLIK,
        image: '/assets/images/payment-methods/blik.png',
        name: 'BLIK',
        tooltip: tooltipForLockedPayment,
      },
      {
        available: false,
        code: PaymentMethodCode.FAST_TRANSFER,
        image: '',
        name: 'Szybki transfer',
        tooltip: tooltipForLockedPayment,
      },
    ];
    if (this.appConfig.config.payment.hiddenMethods.length > 0) {
      this.methods = this.methods.filter(value => !this.appConfig.config.payment.hiddenMethods.includes(value.code));
    }
  }

  private containsAutoPayment(): boolean {
    return !!this.methods.find((m) => m.code === PaymentMethodCode.AUTO_PAYMENT);
  }

  private getAvailableMethod(method: PaymentMethod, payment: Payment): PaymentMethod {

    if (this.appConfig.config.payment.disabledMethods.includes(method.code)) {
      return { ...method, available: false, tooltip: Tooltip.DISABLED_METHOD_TOOLTIP };
    }

    const available = method.code !== PaymentMethodCode.TWISTO;
    method = { ...method, available };

    return this.buildPaymentMethod(method, payment);
  }

  private buildPaymentMethod(method: PaymentMethod, payment: Payment): PaymentMethod {
    const availableMethods = payment.availablePaymentMethods;
    method.available = this.checkMethodAvailability(method, availableMethods);
    method.tooltip = method.available ? '' : this.getTooltip(method, payment);

    return method;
  }

  private checkMethodAvailability(method: PaymentMethod, availableMethods: AvailablePaymentMethods) {
    if (method.code === PaymentMethodCode.CREDIT_CARD) {
      return (
        PaymentMethodService.paymentMethodAvailable(availableMethods, PaymentMethodCode.EXISTING_CREDIT_CARD) ||
        PaymentMethodService.paymentMethodAvailable(availableMethods, PaymentMethodCode.ONE_TIME_CREDIT_CARD)
      );
    } else if (method.code === PaymentMethodCode.AUTO_PAYMENT) {
      return true;
    }
    return PaymentMethodService.paymentMethodAvailable(availableMethods, method.code);
  }

  private getTooltip(method: PaymentMethod, payment: Payment): string {
    const { blik, fastTransfer, defer } = this.appConfig.config.payment.minMethodAmount;

    switch (method.code) {
      case PaymentMethodCode.BLIK:
        return this.checkMinAmount(payment.totalAmount, blik);
      case PaymentMethodCode.FAST_TRANSFER:
        return this.checkMinAmount(payment.totalAmount, fastTransfer);
      case PaymentMethodCode.DEFER:
        return this.checkMinAmount(payment.totalAmount, defer);
      case PaymentMethodCode.CREDIT_CARD:
        return payment.unverifiedCustomerBillers?.length > 0 ? Tooltip.CREDIT_CARD_UNVERIFIED_BILLER : '';
      default:
        return '';
    }
  }

  private checkMinAmount(paymentAmount: number, minMethodAmount: number): string {
    return paymentAmount < minMethodAmount ? Tooltip.AMOUNT_TOO_LOW : '';
  }
}
